import { ComponentProps, ComponentType, createContext, useContext } from 'react';
import BasePortableText from 'react-portable-text';

import { urlFor } from '@root/lib/sanity';
import ColorOfTheYear from 'components/ColorOfTheYear';
import DoubleHeader from 'components/typography/DoubleHeader';
import DoubleLineHeader from 'components/typography/DoubleLineHeader';
import SectionTitle from 'components/typography/SectionTitle';
import FaqItem from 'components/FaqItem';
import GentuxEm from 'components/GentuxEm';
import HTODisclaimer from 'components/HTODisclaimer';
import Layout1x1 from 'components/Layout1x1';
import Layout2x1 from 'components/Layout2x1';
import Layout2x1ImageLeft from 'components/Layout2x1ImageLeft';
import Layout2x1ImageRight from 'components/Layout2x1ImageRight';
import Layout2x2 from 'components/Layout2x2';
import LayoutFaq from 'components/LayoutFaq';
import FeaturedReviews from 'components/FeaturedReviews';
import Line from 'components/Line';
import NextSteps from 'components/NextSteps';
import Picture from 'components/Picture';
import Quote from 'components/Quote';
import ResponsiveContentBlocksWithTitle from 'components/ResponsiveContentsWithTitle';
import SalesCalloutGroomsFree from 'components/SalesCalloutGroomsFree';
import SalesCalloutHTO from 'components/SalesCalloutHTO';
import SalesCalloutInspiration from 'components/SalesCalloutInspiration';
import SalesCalloutReviews from 'components/SalesCalloutReviews';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import SalesCalloutAlternateNew from 'components/SalesCalloutAlternateNew';
import {
  Button as ButtonProps,
  Bundle as SanityBundle,
  DoubleHeader as DoubleHeaderProps,
  FaqItem as FaqItemType,
  FeaturedReviewItem as FeaturedReviewItemType,
  FeaturedReviews as FeaturedReviewsType,
  GalleryProductBlock as GalleryProductBlockProps,
  HeroSlider as HeroSliderProps,
  HiwItem as HIWItemProps,
  Layout2x1ImageLeft as Layout2x1ImageLeftProps,
  Layout2x1ImageRight as Layout2x1ImageRightProps,
  Layout3x1 as Layout3x1Props,
  LayoutFaq as LayoutFaqProps,
  Layout1x1 as Layout1x1Props,
  Layout2x1 as Layout2x1Props,
  Layout2x2 as Layout2x2Props,
  NumberedList as NumberedListProps,
  NumberedListItem as NumberedListItemProps,
  Picture as PictureProps,
  Product as SanityProduct,
  ProductCallout as ProductCalloutType,
  Quote as QuoteProps,
  SalesCallout,
  SalesCalloutAlternate as SalesCalloutAlternateProps,
  Space as SpaceProps,
  StyleBlock as StyleBlockProps,
  ThreeImagesWithText as ThreeImagesWithTextProps,
  Video as VideoProps,
  VisualAsset as VisualAssetType,
  WeddingTable as WeddingTableProps,
  YotpoReviewsCarousel as YotpoReviewsCarouselProps,
  Tab as TabSanityProps,
  Tabs as TabsSanityProps,
  WhatsTrending as WhatsTrendingProps,
  WhatsTrendingItem as WhatsTrendingItemProps,
  BuyYourSuitOrTux as BuyYourSuitOrTuxProps,
  YotpoStarRatings as YotpoStarRatingsProps,
  YotpoUgcGallery as YotpoUgcGalleryProps,
  Testimonials as TestimonialsType,
  HomepageShowroomCallout as HomepageShowroomCalloutSanityProps,
} from '@root/sanity/sanity.types';
import type { Breakpoints, Product, SanityLayoutComponentSpacing } from '@root/src/types';
import type { SerializerData } from './serializerData';
import type { TwoOrThreeItems } from 'components/homepage/BuySuitOrTuxSection';
import SanityButton from 'components/sanity/Button';
import SanityLink from 'components/sanity/Link';
import QuoteCallout from '@root/src/components/QuoteCallout';
import Carousel from 'components/Carousel';
import GalleryProductBlock from 'components/GalleryProductBlock';
import Layout3x1 from 'components/Layout3x1';
import ProductCallout from 'components/ProductCallout';
import FitProfile from 'partials/assets/FitProfile';
import Video from 'components/Video';
import HIWItem from 'components/HIWItem';
import IllustrationDesign from 'components/IllustrationDesign';
import IllustrationFit from 'components/IllustrationFit';
import IllustrationShip from 'components/IllustrationShip';
import ThreeImagesWithText from 'components/ThreeImagesWithText';
import NumberedList from 'components/NumberedList';
import WeddingTable from 'components/WeddingTable';
import YotpoReviewsBadge from 'components/YotpoReviewsBadge';
import YotpoStarRatings from 'components/YotpoStarRatings';
import YotpoReviewsCarousel from 'components/YotpoReviewsCarousel';
import Tabs from 'components/sanity/Tabs';
import YotpoImageGallery from '../yotpo/ImageGallery';
import HomePageTabs from 'components/HomePageTabs';
import { TrendingBundleSlider, type TrendingBundle } from 'components/homepage/WhatsTrending';
import BuySuitOrTuxSection from 'components/homepage/BuySuitOrTuxSection';
import { Testimonials } from 'components/Testimonials';
import RentOrBuyModal from 'components/homepage/RentOrBuyModal';
import HeroImageCarousel from 'components/homepage/HeroImageCarousel';
import { getSourcesForImage, HeroImage } from 'components/homepage/HeroImage';
import ShowroomCallout from 'components/homepage/ShowroomCallout';
import { buildPresentationClass, PresentationRule } from '@root/src/sanity/presentation';
import { twMerge } from 'tailwind-merge';
import TrackedLink from './TrackedLink';

export type PortableTextProps = ComponentProps<typeof BasePortableText>;

export type Serializers = {
  [key: string]: ComponentType<any>;
};

type SerializerOptions = {
  data: SerializerData;
  serializers?: Serializers;
};

export const SerializerContext = createContext<SerializerOptions>({ data: {} });

export const useSerializerData = () => {
  const { data } = useContext(SerializerContext);

  return data;
};

/**
 * Wraps `<PortableText />` with default values for certain props
 *
 * `ignoreUnknownTypes` is set to `true` so we can conditionally disable "unknown
 * type" exceptions via the `unknownType` serializer. This will allow us to test
 * new block types locally without affecting other developers or the CI pipeline.
 *
 * `serializers` provides the component serializers that are defined below.
 */
export const PortableText = (props: PortableTextProps & { children?: any }) => {
  const context = useContext(SerializerContext);

  const serializers = {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    ...components,
    ...(context.serializers ?? {}),
    ...(props.serializers ?? {}),
  };

  return <BasePortableText className={props.className} content={props.content} ignoreUnknownTypes={true} serializers={serializers} />;
};

/**
 * Returns a boolean indicating whether an unknown block type should cause an
 * exception to be thrown
 *
 * #### Behavior by environment
 * |                | Client                              | Server                                |
 * |----------------|-------------------------------------|---------------------------------------|
 * | **Local**      | Shows an error on the page          | Logs a warning to the terminal        |
 * | **QA**         | Logs an error to the console        | Logs a warning to the terminal        |
 * | **Production** | Logs an error to the console [1]    | Throws an exception and stops a build |
 *
 * [1] This condition will never be reached since the build will fail and the site
 *     won't be deployed, but if it was reached then this is how it would behave.
 */
export const shouldThrowOnUnknownType = () => typeof window !== 'undefined' || process.env.ENVIRONMENT == 'production';

const getPresentationClasses = (presentation: PresentationRule[]) => presentation.map((rule) => buildPresentationClass(rule));

const getSalesCallout = (salesCallout: SalesCallout, ctaPageName: string) => {
  switch (salesCallout.type) {
    case 'groomsFree':
      return <SalesCalloutGroomsFree ctaPageName={ctaPageName} />;
    case 'hto':
      return <SalesCalloutHTO ctaPageName={ctaPageName} />;
    case 'swatches':
      return <SalesCalloutSwatches ctaPageName={ctaPageName} />;
    case 'inspiration':
      return <SalesCalloutInspiration ctaPageName={ctaPageName} />;
    case 'reviews':
      return <SalesCalloutReviews ctaPageName={ctaPageName} />;
  }
};

const getVisualAsset = (assetType: string) => {
  switch (assetType) {
    case 'fitProfile':
      return <FitProfile />;
    case 'designIllustration':
      return <IllustrationDesign />;
    case 'fitIllustration':
      return <IllustrationFit />;
    case 'shipIllustration':
      return <IllustrationShip />;
  }
};

const getPadding = <T extends SanityLayoutComponentSpacing>(spaceBottom: T['spaceBottom'], spaceTop: T['spaceTop']) =>
  `${spaceTop === 'collapsed' ? 'pt-0' : ''} ${spaceBottom === 'collapsed' ? 'pb-0' : ''}`.trim();

const GalleryProductBlockSerializer = (props: GalleryProductBlockProps) => {
  const { bundles: allBundles = [], products: allProducts = [] } = useSerializerData();

  let bundle;
  let products;

  const sanityBundle = props?.bundle as unknown as SanityBundle;
  const sanityProducts = props?.products as unknown as SanityProduct[];

  if (sanityBundle?.bundleId?.current) {
    bundle = allBundles.find(({ id }) => id === Number(sanityBundle.bundleId!.current));
  }

  if (sanityProducts?.length > 0) {
    products = sanityProducts.map(({ catalogNumber }) => allProducts.find(({ sku }) => sku === catalogNumber!.current));
  }

  return (
    <GalleryProductBlock
      bundle={bundle}
      content={<PortableText content={props.content!} />}
      ctaButton={props.ctaButton}
      horizontalImg={{
        alt: props?.horizontalImage?.alt ?? '',
        url: urlFor(props?.horizontalImage?.asset).url()!,
      }}
      polaroidImg={{
        alt: props?.polaroidImage?.alt ?? '',
        url: urlFor(props?.polaroidImage?.asset).url()!,
      }}
      products={products as Product[]}
      reversed={Boolean(props.isReversed)}
      title={props.title}
      trackerPageName={props.trackerPageName}
    />
  );
};

const PictureSerializer = (props: PictureProps) => {
  const fixedWidthParam = props.fixedImage ? `?w=${props.fixedPictureValue}` : '';

  const altText = props?.alt ? props.alt : '';

  if (!props?.asset) {
    return null;
  }

  let srcSet: Breakpoints = {};

  if (props.customWidths) {
    const { _type, ...widths } = props.customWidths;

    srcSet = widths;
  }

  //Default
  let finalPicture = (
    <Picture
      url={urlFor(props.asset).url()!}
      alt={altText}
      polaroid={props.polaroid}
      hero={props.hero}
      className={`aspect-ratio-7by5${props.portrait ? ` xs:aspect-ratio-5by7` : ''}`}
      srcSet={srcSet}
    />
  );

  if (props.asIs) {
    finalPicture = <img src={urlFor(props.asset).url()} alt={altText} />;
  } else if (props.fixedImage) {
    finalPicture = <img loading="lazy" src={`${urlFor(props.asset).url() + fixedWidthParam}`} alt={altText} />;
  }

  if (props.link?.href) {
    finalPicture = (
      <SanityLink
        href={props.link.href}
        elementLabel={props.link.elementLabel}
        includeQueryString={props.link.includeQueryString}
        trackerClass={props.link.trackerClass}
      >
        {finalPicture}
      </SanityLink>
    );
  }

  return finalPicture;
};

const LinkSerializer = (props: any) => {
  const className = [props.trackerClass, props.color, props.underline ? 'underline' : ''].filter(Boolean).join(' ') || undefined;

  return (
    <TrackedLink
      className={className}
      elementLabel={props.elementLabel}
      href={props.href}
      includeQueryString={props.includeQueryString}
      reloadDocument
      {...(props.newTab && { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {props.children}
    </TrackedLink>
  );
};

const YotpoUgcGallerySerializer = (props: YotpoUgcGalleryProps) => {
  const { yotpoAlbums = [], bundles = [] } = useSerializerData();
  const { header, album: albumId } = props;
  const item = props.item as unknown as SanityBundle;

  if (!item) {
    console.warn('Yotpo UGC galleries used on pages outside of /collection must have an item assigned');
    return null;
  }

  const album = yotpoAlbums.find((value) => value.id === albumId);
  const bundle = bundles.find(({ id }) => Number(item.bundleId?.current) === id);

  if (!album || !bundle) {
    console.warn('yotpoUgcGallery block is referencing a non-existent album and/or bundle');
    return null;
  }

  return <YotpoImageGallery heading={header} album={album.images} item={bundle} />;
};

const WhatsTrendingSerializer = (props: WhatsTrendingProps) => {
  const { bundles: allBundles = [] } = useSerializerData();

  const tabs = (props.trendingTabs ?? []).map((tab) => {
    const bundles: TrendingBundle[] = (tab.items ?? []).map((item: WhatsTrendingItemProps) => {
      const sanityBundle = item.bundle as unknown as SanityBundle;
      const externalBundle = sanityBundle?.bundleId?.current
        ? allBundles.find(({ id }) => id === Number(sanityBundle.bundleId!.current))
        : undefined;

      return {
        title: item.title || externalBundle?.display_name || externalBundle?.name || '',
        cost: item.cost || externalBundle?.cost || '',
        image: item.image?.asset ? urlFor(item.image.asset).url()! : '',
        urlSlug: item.urlSlug || externalBundle?.url_slug || '',
      };
    });

    return {
      content: <TrendingBundleSlider bundles={bundles} />,
      title: tab.tabLabel ?? '',
    };
  });

  return (
    <div>
      <SectionTitle line color="black" primary={props.heading ?? ''} tertiary={props.subheading} className="my-32 md:my-64" />
      <HomePageTabs className="mt-16 mb-32 px-32" data={tabs} />
    </div>
  );
};

const LayoutFaqSerializer = (props: LayoutFaqProps) => {
  const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);
  let presentation: string[] = [];

  if (props.presentation) {
    presentation = getPresentationClasses(props.presentation);
  }

  return (
    <div className={twMerge('p-section', props.backgroundColor, padding, ...presentation)}>
      <LayoutFaq
        faqSingle={props.faqSingle && <PortableText content={props.faqSingle} />}
        titleType={props.titleType}
        title={props.title}
        sectionTitleColor={props.sectionTitleColor}
        cta={props.enableCta && props.cta?.href && <PortableText content={[props.cta]} />}
        faqItems={props.faqItems && <PortableText content={props.faqItems} />}
        full={props.full}
      >
        {props.children && <PortableText content={props.children} />}
      </LayoutFaq>
    </div>
  );
};

export const components = {
  unknownType: (props: any) => {
    if (shouldThrowOnUnknownType()) {
      const blockType = props.node?._type ?? "Couldn't determine name of type";

      throw new Error(`No serializer is defined for the following type: ${blockType}`);
    }

    return null;
  },
  button: (props: ButtonProps) => {
    if (!props.text) {
      return null;
    }

    return <SanityButton {...props} />;
  },

  /**
   * Gallery Product Callout Block
   */
  galleryProductBlock: GalleryProductBlockSerializer,

  picture: PictureSerializer,

  layout1x1: (props: Layout1x1Props) => {
    const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);

    return (
      <div className={`${props.hero ? 'p-section-hero' : 'p-section'} ${padding} ${props.backgroundColor}`}>
        <Layout1x1 hero={props.hero}>{props.children && <PortableText content={props.children} />}</Layout1x1>
      </div>
    );
  },

  layout2x1: (props: Layout2x1Props) => {
    const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);
    let presentation: string[] = [];

    if (props.presentation) {
      presentation = getPresentationClasses(props.presentation);
    }

    return (
      <div className={twMerge('p-section', padding, props.backgroundColor, ...presentation)}>
        <Layout2x1
          alignment={props.alignment}
          block1={<PortableText content={props.blockContent1} />}
          block2={<PortableText content={props.blockContent2} />}
          variant={props.variant}
        >
          {props.children && <PortableText content={props.children} />}
        </Layout2x1>
      </div>
    );
  },

  layout2x1ImageLeft: (props: Layout2x1ImageLeftProps) => {
    const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);

    if (props?.bottomAlignLayout) {
      if (!props?.image?.asset) {
        return null;
      }

      const im = (
        <div className="flex h-full self-end aspect-ratio-7by5">
          <Picture
            hero
            noAspectRatioClass
            url={urlFor(props.image.asset).url()!}
            alt={props.image?.alt ? props.image?.alt : ''}
            focalPointY={0}
            className="overflow-hidden"
          />
        </div>
      );

      return (
        <div className={`${props.backgroundColor}`}>
          <Layout2x1ImageLeft hero className={`p-section-hero pb-0 ${padding}`} image={im}>
            {props.children && <PortableText className={`${!props.spaceBottom ? 'pb-64' : ''}`} content={props.children} />}
          </Layout2x1ImageLeft>
        </div>
      );
    }

    const content = props[`${props.mediaType}` as keyof Layout2x1ImageRightProps] as any;

    return (
      <div className={`${props.hero ? 'p-section-hero' : 'p-section'} ${padding} ${props.backgroundColor}`}>
        <Layout2x1ImageLeft hero={props.hero} image={<PortableText content={content} />}>
          {props.children && <PortableText content={props.children} />}
        </Layout2x1ImageLeft>
      </div>
    );
  },
  layout2x1ImageRight: (props: Layout2x1ImageRightProps) => {
    const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);

    if (props?.bottomAlignLayout) {
      if (!props?.image?.asset) {
        return null;
      }

      //Moving the aspect ratio class to the outside div so the image can fill the div without the top of the image cropped out.
      const im = (
        <div className="flex h-full aspect-ratio-7by5">
          <Picture
            hero
            noAspectRatioClass
            url={urlFor(props.image.asset).url()!}
            alt={`${props.image.alt ? props.image.alt : ''}`}
            focalPointY={0}
            className="overflow-hidden"
          />
        </div>
      );

      return (
        <div className={`${props.backgroundColor}`}>
          <Layout2x1ImageRight className={`p-section-hero pb-0 ${padding}`} hero image={im}>
            {props.children && <PortableText className={`${!props.spaceBottom ? 'pb-64' : ''}`} content={props.children} />}
          </Layout2x1ImageRight>
        </div>
      );
    }

    const content = props[`${props.mediaType}` as keyof Layout2x1ImageRightProps] as any;

    return (
      <div className={`${props.hero ? 'p-section-hero' : 'p-section'} ${padding} ${props.backgroundColor}`}>
        <Layout2x1ImageRight hero={props.hero} image={<PortableText content={content} />}>
          {props.children && <PortableText content={props.children} />}
        </Layout2x1ImageRight>
      </div>
    );
  },
  numberedList: (props: NumberedListProps) => {
    const items = props.items.map(({ content, headerText, image }: NumberedListItemProps) => ({
      content: <PortableText content={content} />,
      headerText,
      image,
    }));

    const aboveContent = props?.content ? <PortableText content={props.content} /> : '';

    return <NumberedList items={items} content={aboveContent} twoColumnGrid={props.twoColumnGrid} className={props?.backgroundColor} />;
  },
  faqItem: (props: FaqItemType) => (
    <FaqItem elementLabel={props.elementLabel} question={props.question} trackerClass={props.trackerClass ?? ''}>
      {props.children && <PortableText content={props.children} />}
    </FaqItem>
  ),
  layoutFaq: LayoutFaqSerializer,
  featuredReviewItem: (props: FeaturedReviewItemType) => {
    const { fixedImage, fixedPictureValue, portrait, asset, alt } = props.image || {};
    const imgWidth: number = fixedImage && fixedPictureValue ? fixedPictureValue : portrait ? 246 : 378;
    const imgSrcSet: Breakpoints = ['lg', 'md', 'sm', 'xs', 'base'].reduce((accum: { [key: string]: number }, curr: string) => {
      accum[curr] = imgWidth;
      return accum;
    }, {});

    return (
      <QuoteCallout
        quote={props.testimonial}
        citation={props.customerName}
        image={
          <div className="inline-block max-w-full border-8 border-white shadow-3 sm:border-16">
            <Picture
              url={urlFor(asset).url()!}
              alt={alt ?? ''}
              focalPointY={0}
              className={`aspect-ratio-${portrait ? '5by7' : '7by5'}`}
              srcSet={imgSrcSet}
            />
          </div>
        }
      />
    );
  },
  featuredReviews: (props: FeaturedReviewsType) => (
    <div className={`py-1 ${props.backgroundColor}`}>
      <FeaturedReviews
        title={props.title}
        featuredReviewItems={
          props.featuredReviewItems && <PortableText content={props.featuredReviewItems} className="mb-64 space-y-64 md:mb-128" />
        }
        showDynamicReviews={props.showDynamicReviews}
        cta={props.cta}
      />
    </div>
  ),
  nextSteps: (props: any) => (
    <div className={props.backgroundColor}>
      <NextSteps
        ctaPageName={props.ctaPageName}
        salesCallout1={getSalesCallout(props.salesCallout1, props.ctaPageName)}
        salesCallout2={getSalesCallout(props.salesCallout2, props.ctaPageName)}
      />
      {props.salesCallout1.type === 'hto' || props.salesCallout2.type === 'hto' ? <HTODisclaimer /> : null}
    </div>
  ),
  homepageShowroomCallout: (props: HomepageShowroomCalloutSanityProps) => (
    <ShowroomCallout
      heading={props.heading}
      subheading={props.subheading}
      copyParagraph={props.copyParagraph && <PortableText content={props.copyParagraph} />}
      copyList={(props.copyList ?? []).map((item) => (
        <PortableText key={item._key} content={item.text} />
      ))}
    />
  ),
  salesCalloutAlternate: (props: SalesCalloutAlternateProps) => (
    <SalesCalloutAlternateNew
      headline={props.headline!}
      image={<PortableText content={[props.image!]} />}
      {...(props.cta?.href && {
        cta: <PortableText content={[props.cta]} className="sales-callout-alternate-button" />,
      })}
    >
      {props.children && <PortableText content={props.children} />}
    </SalesCalloutAlternateNew>
  ),
  doubleHeader: (props: DoubleHeaderProps) => (
    <DoubleHeader
      smallTitle={props?.smallTitle ? props.smallTitle : ''}
      largeTitle={props?.largeTitle ? props.largeTitle : ''}
      hero={props.hero}
      boldSmallTitle={props?.boldSmallTitle}
    />
  ),
  quote: (props: QuoteProps) => (
    <Quote citationName={props.citationName} citationTitle={props.citationTitle}>
      {props.quote}
    </Quote>
  ),
  normal: ({ children }: any) => <p className="mb-16">{children}</p>,
  nospace: ({ children }: any) => <p>{children}</p>,
  sm: ({ children }: any) => <p className="text-sm mb-16">{children}</p>,
  h1: ({ children }: any) => <h1 className="text-h1 mb-16">{children}</h1>,
  h2: ({ children }: any) => <h2 className="text-h2 mb-16">{children}</h2>,
  h3: ({ children }: any) => <h3 className="text-h3 mb-16">{children}</h3>,
  h4: ({ children }: any) => <h4 className="text-h4 mb-16">{children}</h4>,
  h1_display: ({ children }: any) => <h1 className="text-h1-display">{children}</h1>,
  h2_display: ({ children }: any) => <h2 className="text-h2-display">{children}</h2>,
  h3_display: ({ children }: any) => <h3 className="text-h3-display">{children}</h3>,
  h4_display: ({ children }: any) => <h4 className="text-h4-display">{children}</h4>,
  normal_h1: ({ children }: any) => <p className="text-h1">{children}</p>,
  normal_h2: ({ children }: any) => <p className="text-h2">{children}</p>,
  normal_h3: ({ children }: any) => <p className="text-h3">{children}</p>,
  normal_h4: ({ children }: any) => <p className="text-h4">{children}</p>,
  normal_h1_display: ({ children }: any) => <p className="text-h1-display">{children}</p>,
  normal_h2_display: ({ children }: any) => <p className="text-h2-display">{children}</p>,
  normal_h3_display: ({ children }: any) => <p className="text-h3-display">{children}</p>,
  normal_h4_display: ({ children }: any) => <p className="text-h4-display">{children}</p>,

  line: () => <Line />,
  space: (props: SpaceProps) => <div className={`${props.verticalSpacingSize}`}>&nbsp;</div>,
  styleBlock: (props: StyleBlockProps) => {
    const partialWhiteBgOuter =
      props.partialWhiteBg === 'right' ? 'xs:bg-panel-6/12-to-right' : props.partialWhiteBg === 'left' ? 'xs:bg-panel-6/12-to-left' : '';
    const partialWhiteBgInner =
      props.partialWhiteBg === 'right' ? 'xs:bg-panel-9/12-to-right' : props.partialWhiteBg === 'left' ? 'xs:bg-panel-9/12-to-left' : '';

    return (
      <div className={`${props.style}${props.container ? ' container' : ''}`}>
        <div className={`${partialWhiteBgOuter}`}>
          {props?.pageBuilder?.map((item: any) => (
            <PortableText content={item} key={item._key} className={`${partialWhiteBgInner}`} />
          ))}
        </div>
      </div>
    );
  },
  link: LinkSerializer,
  carousel: (props: any) => (
    <Carousel>
      {props.children.map((img: any) => (
        <img loading="lazy" src={`${urlFor(img.asset).url()}?w=376`} alt={img.alt} key={img._key} />
      ))}
    </Carousel>
  ),
  colorOfTheYear: (props: any) => (
    <ColorOfTheYear title={props.title} image={props.image}>
      {props?.description && <PortableText content={props.description} />}
    </ColorOfTheYear>
  ),
  gentuxem: (props: any) => <GentuxEm>{props.children}</GentuxEm>,
  htoDisclaimer: (props: any) => <HTODisclaimer textOnly={props.textOnly ?? true} />,
  layout2x2: (props: Layout2x2Props) => (
    <Layout2x2
      className={`${!props.spaceBottom ? 'mb-64' : ''} ${props.backgroundColor}`}
      block1={props.blockContent1 && <PortableText content={props.blockContent1} />}
      block2={props.blockContent2 && <PortableText content={props.blockContent2} />}
      block3={props.blockContent3 && <PortableText content={props.blockContent3} />}
      block4={props.blockContent4 && <PortableText content={props.blockContent4} />}
    />
  ),
  layout3x1: (props: Layout3x1Props) => {
    const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);

    return (
      <div className={`p-section ${props.backgroundColor} ${padding}`}>
        <Layout3x1
          block1={<PortableText className="contents" content={[props.blockContent1[`${props.blockContent1.blockType}`]!]} />}
          block2={<PortableText className="contents" content={[props.blockContent2[`${props.blockContent2.blockType}`]!]} />}
          block3={<PortableText className="contents" content={[props.blockContent3[`${props.blockContent3.blockType}`]!]} />}
          matchHeight={props.matchHeight}
        >
          {props.children && (
            <div className={props.childrenStyle}>
              <PortableText content={props.children} />
            </div>
          )}
        </Layout3x1>
      </div>
    );
  },
  productCallout: (props: ProductCalloutType) => (
    <ProductCallout
      header={props.header}
      elementLabel={props.link.elementLabel}
      linkHref={props.link.href}
      linkText={props.link.title}
      newTab={props.link.newTab}
      trackerClass={props.link.trackerClass ?? ''}
      imgUrl={urlFor(props.image?.asset).url()}
      imgAlt={props.image?.alt ?? ''}
    />
  ),
  visualAsset: ({ alignment, assetType, link }: VisualAssetType) => {
    const visualAsset = getVisualAsset(assetType!);
    const node = link?.href ? (
      <TrackedLink href={link.href} className={link.trackerClass ?? ''} elementLabel={link.elementLabel}>
        {visualAsset}
      </TrackedLink>
    ) : (
      visualAsset
    );

    const justifyMap: Record<string, string> = {
      center: 'justify-center',
      right: 'justify-end',
    };

    const justify = alignment ? justifyMap[alignment] : undefined;

    return justify ? <div className={`flex ${justify}`}>{node}</div> : node;
  },
  video: (props: VideoProps) => <Video title={props.title} url={props.url} videotype={props.videotype} />,
  hiwItem: (props: HIWItemProps) => (
    <HIWItem
      image={<PortableText content={[props[props.mediaType]!]} />}
      description={props.description && <PortableText content={props.description} />}
    />
  ),
  tabs: (props: TabsSanityProps) => {
    if (props.items?.length > 0) {
      const data = props.items.map(({ tabTitle, content }: TabSanityProps) => ({
        content: <PortableText content={content} />,
        title: tabTitle,
      }));

      return (
        <div className="m-section-hero container flex flex-wrap items-center justify-around">
          <Tabs data={data} />
        </div>
      );
    }
  },
  testimonials: (props: TestimonialsType) => (
    <Testimonials heading={props.heading} items={props.items} cta={props.cta?.href && <PortableText content={[props.cta]} />} />
  ),
  textColor: (props: any) => <span className={props.color}>{props.children}</span>,
  threeImagesWithText: (props: ThreeImagesWithTextProps) => (
    <ThreeImagesWithText
      textContent={<PortableText content={props.textContent} />}
      image1={<PortableText content={[props.image1[props.image1.mediaType]!]} />}
      image2={<PortableText content={[props.image2[props.image2.mediaType]!]} />}
      image3={<PortableText content={[props.image3[props.image3.mediaType]!]} />}
    />
  ),
  buyYourSuitOrTux: (props: BuyYourSuitOrTuxProps) => {
    const items = (props.panels ?? []).map((panel) => ({
      imgUrl: panel.image?.asset ? urlFor(panel.image.asset).url()! : '',
      imgAlt: panel.image?.alt ?? '',
      btnText: panel.button?.text ?? '',
      elementLabel: panel.button?.elementLabel,
      href: panel.button?.href ?? '',
      trackerClass: panel.button?.trackerClass ?? '',
    }));

    const { newCollectionCTA, rentOrBuyModal } = props;

    return (
      <div className="buy-suit-or-tux bg-gray-light">
        <BuySuitOrTuxSection
          title={newCollectionCTA?.heading ?? ''}
          subtitle={newCollectionCTA?.subheading ?? ''}
          primaryCta={<SectionTitle primary={props.heading ?? ''} tertiary={props.subheading} color="black" className="text-left" />}
          secondaryCta={
            rentOrBuyModal ? (
              <RentOrBuyModal
                heading={rentOrBuyModal.heading}
                subheading={rentOrBuyModal.subheading}
                rentList={rentOrBuyModal.rentList}
                buyList={rentOrBuyModal.buyList}
                footerRentButton={
                  rentOrBuyModal.footerRentButton && <SanityButton className="w-full" {...rentOrBuyModal.footerRentButton} />
                }
                footerBuyButton={rentOrBuyModal.footerBuyButton && <SanityButton className="w-full" {...rentOrBuyModal.footerBuyButton} />}
              />
            ) : null
          }
          items={items as TwoOrThreeItems<(typeof items)[0]>}
        />
      </div>
    );
  },
  responsiveContentBlocks: (props: any) => {
    const spacingClasses = props.advancedLayout
      ? (props.customClasses ?? '')
      : `p-section ${getPadding(props.spaceBottom, props.spaceTop)}`;

    let title;
    if (props.titleType === 'doubleLineHeader') {
      title = <DoubleLineHeader className="pb-16 md:pb-32">{props.title}</DoubleLineHeader>;
    } else if (props.titleType === 'sectionTitle') {
      const hasSubtitle = Boolean(props.sectionTitleSecondary || props.sectionTitleTertiary);
      const hasDefaultTopSpace = !props.advancedLayout && props.spaceTop !== 'collapsed';
      const sectionTitleClassName = [hasDefaultTopSpace && 'mt-0', !hasSubtitle && 'mb-16 md:mb-64'].filter(Boolean).join(' ') || undefined;

      title = (
        <SectionTitle
          className={sectionTitleClassName}
          color={props.sectionTitleColor}
          line={props.sectionTitleLine}
          primary={props.title}
          secondary={props.sectionTitleSecondary}
          tertiary={props.sectionTitleTertiary}
        />
      );
    }

    let items;
    let contents;

    if (props.contentMode === 'items' && props.items) {
      items = props.items.map((item: any) => {
        const hasCta = item.enableCta && item.cta?.href && item.cta?.text;
        return {
          content: item.content,
          ctaComponent: hasCta ? () => <SanityButton {...item.cta} /> : undefined,
          elementLabel: item.elementLabel,
          href: item.href,
          picture: item.image?.asset ? (
            <Picture alt={item.image.alt ?? ''} className="aspect-ratio-1by1" url={urlFor(item.image.asset).url()!} />
          ) : undefined,
          title: item.title,
        };
      });
    }

    if (props.contentMode === 'contents' && props.contents) {
      const textAlignMap: Record<string, string> = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      };

      const equalHeightWrapperClass = 'relative my-24 h-full px-32 md:my-0 md:pb-64 lg:px-40';

      const ctaWrapperAlignMap: Record<string, string> = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
        full: '',
      };

      const equalHeightCtaPositionMap: Record<string, string> = {
        left: 'md:absolute md:bottom-0 md:left-0 md:mt-0',
        center: 'md:absolute md:bottom-0 md:left-1/2 md:mt-0 md:-translate-x-1/2 md:transform',
        right: 'md:absolute md:bottom-0 md:right-0 md:mt-0',
        full: 'md:absolute md:bottom-0 md:left-0 md:right-0 md:mt-0',
      };

      contents = props.contents.map((entry: any, i: number) => {
        const baseWrapperClass = entry.advancedStyling ? (entry.customClasses ?? '') : (textAlignMap[entry.alignment] ?? '');
        const wrapperClass = `${baseWrapperClass} ${props.equalHeight ? equalHeightWrapperClass : ''}`.trim();

        const media =
          entry.media?.mediaType && entry.media[entry.media.mediaType] ? (
            <PortableText content={[entry.media[entry.media.mediaType]]} />
          ) : null;

        const hasCta = entry.enableCta && entry.cta?.href && entry.cta?.text;
        const ctaAlignment = entry.ctaAlignment ?? 'center';
        const ctaButtonClass = [
          'mt-24',
          ctaAlignment === 'full' ? 'flex w-full' : '',
          props.equalHeight ? equalHeightCtaPositionMap[ctaAlignment] : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div key={i} className={wrapperClass}>
            {entry.title && <h3 className="text-h3 mb-16">{entry.title}</h3>}
            {media}
            {entry.content && <PortableText content={entry.content} />}
            {hasCta && (
              <div className={ctaWrapperAlignMap[ctaAlignment] || undefined}>
                <SanityButton {...entry.cta} className={ctaButtonClass} />
              </div>
            )}
          </div>
        );
      });
    }

    return (
      <div className={`${props.backgroundColor ?? ''} ${spacingClasses}`.trim()}>
        <ResponsiveContentBlocksWithTitle contents={contents} items={items} style="contained" title={title} variant={props.variant} />
      </div>
    );
  },
  collectionPageContents: () => null,
  whatsTrending: WhatsTrendingSerializer,
  weddingTable: (props: WeddingTableProps) => {
    const items = props.items;
    const content = props?.content ? <PortableText content={props.content} /> : undefined;
    const backgroundColor = props?.backgroundColor;

    return <WeddingTable items={items} content={content} backgroundColor={backgroundColor} />;
  },
  yotpoReviewsBadge: () => <YotpoReviewsBadge />,
  yotpoReviewsCarousel: (props: YotpoReviewsCarouselProps) => <YotpoReviewsCarousel count={props.count} mode={props.mode} />,
  yotpoStarRatings: (props: YotpoStarRatingsProps) => {
    return <YotpoStarRatings alignment={props.alignment} />;
  },
  yotpoUgcGallery: YotpoUgcGallerySerializer,
  heroSlider: (props: HeroSliderProps) => {
    const slides = props.slides.map((slide) => {
      const image = <HeroImage sources={getSourcesForImage(slide.image.asset)} alt={slide.image.alt} />;

      return {
        imagePosition: slide.imagePosition,
        colorTheme: slide.colorTheme,
        overline: slide.overline,
        description: slide.description,
        headline: (
          <PortableText
            content={slide.headline}
            serializers={{
              container: ({ children }: any) => <>{children}</>,
              normal: ({ children }: any) => <>{children}</>,
              lineBreak: () => <br className="hidden xs:inline" />,
            }}
          />
        ),
        cta: slide.cta.text ? (
          <>
            <SanityButton {...slide.cta} className="mt-16 w-full sm:w-auto sm:min-w-1/2" />
            {slide.hasSecondaryCta && slide.secondaryCta && (
              <SanityButton {...slide.secondaryCta} className="mt-16 w-full sm:w-auto sm:min-w-1/2" />
            )}
          </>
        ) : null,
        image: slide.cta.href ? (
          <SanityLink
            href={slide.cta.href!}
            elementLabel={slide.cta.elementLabel}
            newTab={slide.cta.newTab}
            includeQueryString={slide.cta.includeQueryString}
          >
            {image}
          </SanityLink>
        ) : (
          image
        ),
      };
    });

    return <HeroImageCarousel slides={slides} />;
  },
};
